import React, { useState } from 'react';
import { Plus, Edit2, Trash2, Eye, X, BookOpen } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { generateId, formatDate } from '../../utils/helpers';

const STATUS_STYLES = {
  draft:     'bg-gray-100 text-gray-600',
  submitted: 'bg-blue-100 text-blue-700',
  approved:  'bg-green-100 text-green-700',
};

const emptyForm = {
  classId: '', subjectId: '', title: '', topic: '', date: '',
  duration: 45, objectives: '', teachingAids: '', activities: '', homework: '', status: 'draft',
};

function LessonPlan() {
  const { lessonPlans, setLessonPlans, classes, subjects, staff } = useApp();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const isTeacher = currentUser?.role === 'teacher';

  const teacherProfile = isTeacher ? staff.find(s => s.id === currentUser?.profileId) : null;
  const availableClasses = isTeacher
    ? classes.filter(c => (teacherProfile?.classIds || []).includes(c.id))
    : classes;
  const availableSubjects = isTeacher
    ? subjects.filter(s => (teacherProfile?.subjectIds || []).includes(s.id))
    : subjects;

  // Filters
  const [filterClass, setFilterClass] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Modal state
  const [modal, setModal] = useState(null); // 'add' | 'edit' | 'view'
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState(null);

  // Scoped plans — teachers see only their own
  const scopedPlans = isTeacher
    ? lessonPlans.filter(p => p.teacherId === currentUser?.profileId)
    : lessonPlans;

  const filtered = scopedPlans.filter(p =>
    (!filterClass   || p.classId   === filterClass) &&
    (!filterSubject || p.subjectId === filterSubject) &&
    (!filterStatus  || p.status    === filterStatus)
  ).sort((a, b) => b.date.localeCompare(a.date));

  const openAdd = () => {
    setForm({
      ...emptyForm,
      classId:   availableClasses[0]?.id  || '',
      subjectId: availableSubjects[0]?.id || '',
      date: new Date().toISOString().split('T')[0],
    });
    setModal('add');
  };
  const openEdit = (p) => { setForm(p); setSelected(p); setModal('edit'); };
  const openView = (p) => { setSelected(p); setModal('view'); };

  const save = () => {
    if (!form.title || !form.classId || !form.subjectId || !form.date) return;
    if (modal === 'add') {
      setLessonPlans(prev => [...prev, {
        ...form,
        id: generateId('LP'),
        teacherId: currentUser?.profileId || '',
        createdAt: new Date().toISOString(),
      }]);
    } else {
      setLessonPlans(prev => prev.map(p => p.id === selected.id ? { ...form } : p));
    }
    setModal(null);
  };

  const remove = (id) => {
    if (window.confirm('Delete this lesson plan?')) setLessonPlans(prev => prev.filter(p => p.id !== id));
  };

  const getClass   = (id) => { const c = classes.find(c => c.id === id);   return c ? `${c.name}-${c.section}` : '—'; };
  const getSubject = (id) => subjects.find(s => s.id === id)?.name || '—';
  const getTeacher = (id) => { const s = staff.find(s => s.id === id); return s ? `${s.firstName} ${s.lastName}` : '—'; };

  const field = (label, key, type = 'text', opts = {}) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {type === 'textarea' ? (
        <textarea rows={3} value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
          placeholder={opts.placeholder || ''} />
      ) : type === 'select' ? (
        <select value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
          {opts.options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input type={type} value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
          placeholder={opts.placeholder || ''} />
      )}
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold">Lesson Plans</h1>
          <p className="text-gray-500 text-sm mt-0.5">{filtered.length} plan{filtered.length !== 1 ? 's' : ''}</p>
        </div>
        {(isTeacher || isAdmin) && (
          <button onClick={openAdd}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium transition-colors">
            <Plus size={15} /> New Plan
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <select value={filterClass} onChange={e => setFilterClass(e.target.value)}
          className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
          <option value="">All Classes</option>
          {availableClasses.map(c => <option key={c.id} value={c.id}>{c.name}-{c.section}</option>)}
        </select>
        <select value={filterSubject} onChange={e => setFilterSubject(e.target.value)}
          className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
          <option value="">All Subjects</option>
          {availableSubjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="submitted">Submitted</option>
          <option value="approved">Approved</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="px-4 py-2 text-left font-semibold">#</th>
              <th className="px-4 py-2 text-left font-semibold">Title / Topic</th>
              {isAdmin && <th className="px-4 py-2 text-left font-semibold">Teacher</th>}
              <th className="px-4 py-2 text-left font-semibold">Class</th>
              <th className="px-4 py-2 text-left font-semibold">Subject</th>
              <th className="px-4 py-2 text-left font-semibold">Date</th>
              <th className="px-4 py-2 text-center font-semibold">Duration</th>
              <th className="px-4 py-2 text-center font-semibold">Status</th>
              <th className="px-4 py-2 text-center font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.map((p, i) => (
              <tr key={p.id} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-center">{i + 1}</td>
                <td className="px-4 py-2">
                  <p className="font-medium">{p.title}</p>
                  <p className="text-gray-400">{p.topic}</p>
                </td>
                {isAdmin && <td className="px-4 py-2">{getTeacher(p.teacherId)}</td>}
                <td className="px-4 py-2">{getClass(p.classId)}</td>
                <td className="px-4 py-2">{getSubject(p.subjectId)}</td>
                <td className="px-4 py-2">{formatDate(p.date)}</td>
                <td className="px-4 py-2 text-center">{p.duration} min</td>
                <td className="px-4 py-2 text-center">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_STYLES[p.status]}`}>
                    {p.status}
                  </span>
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => openView(p)} className="p-1.5 hover:bg-blue-50 rounded-lg text-blue-600" title="View"><Eye size={14}/></button>
                    {(isTeacher || isAdmin) && (
                      <>
                        <button onClick={() => openEdit(p)} className="p-1.5 hover:bg-indigo-50 rounded-lg text-indigo-600" title="Edit"><Edit2 size={14}/></button>
                        <button onClick={() => remove(p.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-red-500" title="Delete"><Trash2 size={14}/></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={isAdmin ? 9 : 8} className="py-10 text-center text-gray-400">No lesson plans found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add / Edit Modal */}
      {(modal === 'add' || modal === 'edit') && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <BookOpen size={18} className="text-indigo-500" />
                {modal === 'add' ? 'New Lesson Plan' : 'Edit Lesson Plan'}
              </h2>
              <button onClick={() => setModal(null)} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={18}/></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {field('Class', 'classId', 'select', { options: availableClasses.map(c => ({ value: c.id, label: `${c.name}-${c.section}` })) })}
                {field('Subject', 'subjectId', 'select', { options: availableSubjects.map(s => ({ value: s.id, label: s.name })) })}
              </div>
              <div className="grid grid-cols-2 gap-4">
                {field('Date', 'date', 'date')}
                {field('Duration (minutes)', 'duration', 'number')}
              </div>
              {field('Lesson Title', 'title', 'text', { placeholder: 'e.g. Introduction to Algebra' })}
              {field('Topic', 'topic', 'text', { placeholder: 'e.g. Variables and Expressions' })}
              {field('Learning Objectives', 'objectives', 'textarea', { placeholder: 'Students will be able to...' })}
              {field('Teaching Aids / Resources', 'teachingAids', 'textarea', { placeholder: 'Whiteboard, charts, lab equipment...' })}
              {field('Activities / Methodology', 'activities', 'textarea', { placeholder: 'Warm-up, explanation, group work...' })}
              {field('Homework / Assignment', 'homework', 'text', { placeholder: 'Optional' })}
              {field('Status', 'status', 'select', { options: [
                { value: 'draft', label: 'Draft' },
                { value: 'submitted', label: 'Submitted' },
                { value: 'approved', label: 'Approved' },
              ]})}
            </div>
            <div className="flex justify-end gap-3 px-5 pb-5">
              <button onClick={() => setModal(null)} className="px-4 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={save} className="px-5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium">
                {modal === 'add' ? 'Create Plan' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {modal === 'view' && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-lg font-bold">{selected.title}</h2>
              <button onClick={() => setModal(null)} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={18}/></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {[['Class', getClass(selected.classId)], ['Subject', getSubject(selected.subjectId)], ['Date', formatDate(selected.date)], ['Duration', `${selected.duration} min`], ['Status', selected.status], ['Teacher', getTeacher(selected.teacherId)]].map(([k, v]) => (
                  <div key={k} className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-400 font-medium uppercase">{k}</p>
                    <p className="font-semibold capitalize mt-0.5">{v}</p>
                  </div>
                ))}
              </div>
              {[['Topic', selected.topic], ['Learning Objectives', selected.objectives], ['Teaching Aids / Resources', selected.teachingAids], ['Activities / Methodology', selected.activities], ['Homework', selected.homework]].map(([k, v]) => v ? (
                <div key={k}>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{k}</p>
                  <p className="bg-gray-50 rounded-lg p-3 text-sm">{v}</p>
                </div>
              ) : null)}
            </div>
            <div className="flex justify-end px-5 pb-5">
              <button onClick={() => setModal(null)} className="px-4 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export default LessonPlan;
